// Taking back the answer you just gave.
//
// Every round in this app is answered with a thumb, at speed, often walking.
// The buttons are large and the wrong one still gets pressed — and until now
// that misfire was permanent in the only way that matters: FSRS believed it.
// A bird you know perfectly well, thumbed into "Again" once, comes back in ten
// minutes, then keeps coming back, and the scheduler you are meant to trust has
// been taught something you know to be false with no way to say so.
//
// So this is one function and its inverse, and everything a quiz view does to
// grade an answer goes through the pair. What comes back:
//
//   the card      restored exactly, or deleted outright when the answer was the
//                 first this species ever got in this skill — see deleteCard
//   the review    removed from the log, so the day's accuracy, the confusion
//                 pairs and the review count never saw it
//   the XP        refunded, including the ten a lucky misfire just paid out
//
// What deliberately does NOT come back is the streak. `touchStreak` records
// that the learner opened the app and answered something today, and taking back
// which bird it was does not make that untrue. Undoing the first answer of a day
// to find the streak gone would be a far worse surprise than the one this whole
// module exists to fix.
//
// One deep, and only the most recent answer, which is all the misfire it is for
// ever needs. `db.undoLastReview` checks the card key rather than trusting the
// caller, so a stale button cannot reach back past the question it belongs to.

import { deleteCard, getCard, putCard, undoLastReview, type CardRecord } from './db';
import { rate, type Rating } from './fsrs';
import { addXp, touchStreak } from './progress';
import { cardKey, type CardKey, type Skill } from '../types';

/** One graded answer, as a quiz view has it at the moment of the tap. */
export interface Answer {
  code: string;
  skill: Skill;
  rating: Rating;
  /** The species the learner named, where there was a list to name it from. */
  picked?: string;
  /** XP this answer pays; the views pay 0 for a wrong one. */
  xp: number;
  now?: Date;
}

/** What has to be put back, kept between the answer and the second thoughts. */
interface Taken {
  key: CardKey;
  /** The card as it stood before, or null when the species had no card at all. */
  before: CardRecord | null;
  xp: number;
}

let last: Taken | null = null;

/**
 * Grade an answer: schedule the card, log the review, pay the XP, mark the day.
 *
 * The whole transaction in one call, so that every mode grades identically and
 * — the reason it exists — so there is one place that knows what taking it back
 * would have to involve.
 */
export async function gradeAnswer(a: Answer): Promise<void> {
  const now = a.now ?? new Date();
  const key = cardKey(a.code, a.skill);
  const existing = await getCard(key);
  // Copied, not referenced: `rate` replaces the record rather than mutating it
  // today, and undo would be a silent no-op the day that stops being true.
  const before: CardRecord | null = existing
    ? { ...existing, card: { ...existing.card }, due: new Date(existing.due) }
    : null;

  await rate(a.code, a.skill, a.rating, now, a.picked);
  await addXp(a.xp);
  await touchStreak(now);

  last = { key, before, xp: a.xp };
}

/** Whether there is an answer to take back. */
export function canUndo(): boolean {
  return last !== null;
}

/**
 * Put the last answer back the way it was. Returns whether anything happened.
 *
 * The review goes first and its refusal is final: if the log's most recent entry
 * isn't the one this undo is for, something has happened in between that this
 * module doesn't understand, and half-undoing an answer — a card rolled back
 * with its review still counted — is worse than not undoing it at all.
 */
export async function undoAnswer(): Promise<boolean> {
  const taken = last;
  if (!taken) return false;
  last = null;

  if (!(await undoLastReview(taken.key))) return false;
  if (taken.before) await putCard(taken.before);
  else await deleteCard(taken.key);
  if (taken.xp) await addXp(-taken.xp);
  return true;
}

/**
 * Forget the last answer without undoing it — on leaving a round, or on moving
 * to the next question. Undo is offered for as long as its own question is on
 * screen and not one moment longer.
 */
export function clearUndo(): void {
  last = null;
}
